<template>
  <div class="thsr-import-page" v-loading="loading">
    <!-- 1. 系統頂部標頭 (Element-UI 經典風格) -->
    <el-card class="header-card" shadow="never">
      <div class="header-content">
        <div class="header-left">
          <div class="header-title-row">
            <i class="el-icon-upload header-title-icon"></i>
            <span class="main-title">高鐵人工監測數據匯入</span>
          </div>
          <div class="meta-info-row">
            <el-tag size="small" type="primary" effect="plain" class="meta-tag">
              <i class="el-icon-office-building"></i> 目標專案：<strong>{{ targetProjectName }}</strong> ({{ THSR_MANUAL_PROJECT_ID }})
            </el-tag>
            <el-tag
              size="small"
              :type="isTreeLoaded ? 'success' : 'warning'"
              effect="plain"
              class="meta-tag"
            >
              <i :class="isTreeLoaded ? 'el-icon-success' : 'el-icon-loading'"></i>
              {{ isTreeLoaded ? `已載入 ${instrumentGlobalMap.size} 個儀器 (${projectStations.length} 個測站)` : '正在載入專案...' }}
            </el-tag>
          </div>
        </div>
        <div class="header-right">
          <el-button
            size="small"
            icon="el-icon-refresh"
            @click="loadProjectTree"
            :loading="treeLoading"
          >
            重新載入專案儀器
          </el-button>
          <el-button
            size="small"
            icon="el-icon-refresh-left"
            @click="resetAll"
            :disabled="!fileName && parsedRecords.length === 0"
          >
            清空輸入
          </el-button>
        </div>
      </div>
    </el-card>

    <!-- 2. 工作流程步驟引導 (Element UI Steps) -->
    <el-card class="steps-card" shadow="never">
      <el-steps :active="currentWorkflowStep" finish-status="success" align-center class="custom-steps">
        <el-step title="上傳檔案" description="選取 Excel 檔案與類型" icon="el-icon-upload2"></el-step>
        <el-step title="指定工作表" description="選取分頁並檢視欄位" icon="el-icon-document-copy"></el-step>
        <el-step title="解析與確認" description="自動匹配邊坡並預檢重複" icon="el-icon-data-analysis"></el-step>
        <el-step title="執行匯入" description="批次寫入高鐵人工專案" icon="el-icon-circle-check"></el-step>
      </el-steps>
    </el-card>

    <!-- 3. 操作配置面板 (兩欄卡片式佈局) -->
    <el-row :gutter="20" class="config-section">
      <!-- 左欄：檔案與儀器類型 -->
      <el-col :xs="24" :sm="24" :md="12">
        <el-card class="panel-card" shadow="never">
          <div slot="header" class="panel-card-header">
            <span class="panel-title"><i class="el-icon-folder-opened"></i> 步驟 1：來源檔案與儀器類別</span>
            <el-tag size="mini" type="info" effect="plain">支援 .xls / .xlsx</el-tag>
          </div>

          <div class="panel-body-content">
            <!-- 上傳區域 -->
            <div class="upload-drop-zone">
              <el-upload
                action=""
                :auto-upload="false"
                :show-file-list="false"
                accept=".xlsx, .xls"
                :on-change="handleFileChange"
                drag
                class="compact-drag"
              >
                <i class="el-icon-upload upload-drag-icon"></i>
                <div class="el-upload__text">
                  將檔案拖曳至此，或 <em>點擊選取檔案</em>
                </div>
              </el-upload>
            </div>

            <div v-if="fileName" class="selected-file-badge">
              <div class="file-info-col">
                <i class="el-icon-document file-icon"></i>
                <div class="file-text">
                  <div class="file-name" :title="fileName">{{ fileName }}</div>
                  <div class="file-sub">{{ sheetNames.length }} 個工作表</div>
                </div>
              </div>
              <el-tag size="mini" type="success" effect="plain">已載入檔案</el-tag>
            </div>

            <!-- 儀器類型選擇器 -->
            <div class="form-item-group">
              <label class="form-label">
                <span>儀器類型 (Instrument Type)</span>
                <span class="label-sub">請選擇欲匯入的監測儀器</span>
              </label>
              <el-select
                v-model="selectedType"
                placeholder="請選擇儀器類型"
                size="medium"
                style="width: 100%"
                @change="handleTypeChange"
              >
                <el-option
                  v-for="item in instrumentTypes"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                >
                  <div class="type-option-item">
                    <span class="type-name">{{ item.label }}</span>
                    <el-tag size="mini" type="info" class="type-tag">{{ item.readingDesc }}</el-tag>
                  </div>
                </el-option>
              </el-select>
            </div>
          </div>
        </el-card>
      </el-col>

      <!-- 右欄：Sheet 選擇與執行解析 -->
      <el-col :xs="24" :sm="24" :md="12">
        <el-card class="panel-card" shadow="never">
          <div slot="header" class="panel-card-header">
            <span class="panel-title"><i class="el-icon-s-operation"></i> 步驟 2：工作表選取與讀值對照</span>
            <span v-if="selectedSheet" class="panel-header-tip">目前分頁：<strong>{{ selectedSheet }}</strong></span>
          </div>

          <div class="panel-body-content">
            <!-- Sheet 選擇器 -->
            <div class="form-item-group">
              <label class="form-label">
                <span>目標工作表 (Sheet Name)</span>
                <span class="label-sub">請指定要載入的 Sheet</span>
              </label>
              <el-select
                v-model="selectedSheet"
                placeholder="請先上傳檔案以讀取分頁"
                size="medium"
                style="width: 100%"
                :disabled="sheetNames.length === 0"
              >
                <el-option
                  v-for="name in sheetNames"
                  :key="name"
                  :label="name"
                  :value="name"
                >
                  <div class="type-option-item">
                    <span>{{ name }}</span>
                    <el-tag
                      v-if="name === 'TEMPLATE_CHECK'"
                      size="mini"
                      type="warning"
                      effect="light"
                    >
                      標準範本分頁
                    </el-tag>
                  </div>
                </el-option>
              </el-select>
            </div>

            <!-- 讀值規範預覽提示框 -->
            <div class="rules-card-box">
              <div class="rules-title"><i class="el-icon-info"></i> {{ currentTypeObj.label }} - 數據讀值規則</div>
              <div class="rules-content">
                <div class="rule-line">
                  <span class="rule-k">目標讀值欄位：</span>
                  <span class="rule-v font-mono text-primary">{{ currentTypeObj.readingDesc }}</span>
                </div>
                <div class="rule-line">
                  <span class="rule-k">邊坡與測站規則：</span>
                  <span class="rule-v">Excel 邊坡單元 → 測站 <code>[邊坡-{{ selectedType }}]</code> → 儀器</span>
                </div>
                <div class="rule-line">
                  <span class="rule-k">重複寫入處理：</span>
                  <span class="rule-v text-warning">資料庫已存在相同時間數據時，預設自動跳過</span>
                </div>
              </div>
            </div>

            <!-- 執行解析按鈕 -->
            <div class="parse-action-row">
              <el-button
                type="primary"
                icon="el-icon-data-analysis"
                size="medium"
                class="action-submit-btn"
                :loading="parsing"
                :disabled="!canParse"
                @click="parseAndValidateSheet"
              >
                {{ parsing ? '正在解析並與資料庫比對中...' : '載入工作表並執行匹配檢驗' }}
              </el-button>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 4. 解析結果指標統計面板 (解析後呈現) -->
    <transition name="el-zoom-in-top">
      <div v-if="parsedRecords.length > 0" class="results-dashboard-section">
        <!-- 統計卡片列 -->
        <el-row :gutter="16" class="metrics-row">
          <!-- 總筆數 -->
          <el-col :xs="12" :sm="6">
            <div
              class="metric-box box-total"
              :class="{ 'active-filter': activeFilter === 'all' }"
              @click="activeFilter = 'all'"
            >
              <div class="metric-top">
                <span class="metric-label">解析總筆數</span>
                <i class="el-icon-tickets metric-icon"></i>
              </div>
              <div class="metric-value font-mono">{{ totalCount }}</div>
              <div class="metric-bottom">包含本頁所有數據項目</div>
            </div>
          </el-col>

          <!-- 可匯入 -->
          <el-col :xs="12" :sm="6">
            <div
              class="metric-box box-ready"
              :class="{ 'active-filter': activeFilter === 'ready' }"
              @click="activeFilter = 'ready'"
            >
              <div class="metric-top">
                <span class="metric-label">正常可匯入</span>
                <i class="el-icon-circle-check metric-icon"></i>
              </div>
              <div class="metric-value font-mono text-success">{{ readyCount }}</div>
              <div class="metric-bottom">測站與儀器匹配成功</div>
            </div>
          </el-col>

          <!-- 重複跳過 -->
          <el-col :xs="12" :sm="6">
            <div
              class="metric-box box-duplicate"
              :class="{ 'active-filter': activeFilter === 'duplicate' }"
              @click="activeFilter = 'duplicate'"
            >
              <div class="metric-top">
                <span class="metric-label">已存在 (跳過)</span>
                <i class="el-icon-warning-outline metric-icon"></i>
              </div>
              <div class="metric-value font-mono text-warning">{{ duplicateCount }}</div>
              <div class="metric-bottom">已存在於資料庫中，預設略過</div>
            </div>
          </el-col>

          <!-- 異常錯誤 -->
          <el-col :xs="12" :sm="6">
            <div
              class="metric-box box-error"
              :class="{ 'active-filter': activeFilter === 'error' }"
              @click="activeFilter = 'error'"
            >
              <div class="metric-top">
                <span class="metric-label">異常 / 錯誤</span>
                <i class="el-icon-circle-close metric-icon"></i>
              </div>
              <div class="metric-value font-mono text-danger">{{ errorCount }}</div>
              <div class="metric-bottom">找不到儀器或讀值非數值</div>
            </div>
          </el-col>
        </el-row>

        <!-- 表格工具列與預覽清單卡片 -->
        <el-card class="table-card" shadow="never">
          <div slot="header" class="table-toolbar-header">
            <div class="toolbar-left">
              <span class="section-title">
                <i class="el-icon-view"></i> 數據檢驗與預覽明細
              </span>
              <el-radio-group v-model="activeFilter" size="mini" class="filter-radio-group">
                <el-radio-button label="all">全部 ({{ totalCount }})</el-radio-button>
                <el-radio-button label="ready">可匯入 ({{ readyCount }})</el-radio-button>
                <el-radio-button label="duplicate">重複跳過 ({{ duplicateCount }})</el-radio-button>
                <el-radio-button label="error">異常錯誤 ({{ errorCount }})</el-radio-button>
              </el-radio-group>
            </div>

            <div class="toolbar-right">
              <el-input
                v-model="searchQuery"
                placeholder="搜尋邊坡 / 測站 / 儀器名稱"
                prefix-icon="el-icon-search"
                size="small"
                clearable
                style="width: 220px;"
              ></el-input>

              <el-button-group size="small">
                <el-button
                  size="small"
                  icon="el-icon-check"
                  @click="toggleSelectAll(true)"
                  :disabled="readyCount === 0"
                >
                  選取全部可匯入
                </el-button>
                <el-button
                  size="small"
                  icon="el-icon-close"
                  @click="toggleSelectAll(false)"
                >
                  取消全選
                </el-button>
              </el-button-group>

              <el-button
                type="success"
                size="small"
                icon="el-icon-upload2"
                class="main-import-btn"
                :disabled="selectedCount === 0"
                @click="openImportConfirmDialog"
              >
                確認匯入已選 ({{ selectedCount }} 筆)
              </el-button>
            </div>
          </div>

          <!-- 專業數據表格 -->
          <el-table
            :data="paginatedRecords"
            stripe
            border
            size="small"
            style="width: 100%"
            class="refined-data-table"
            :row-class-name="tableRowClassName"
          >
            <!-- 勾選框 -->
            <el-table-column label="選取" width="55" align="center">
              <template #default="scope">
                <el-checkbox
                  v-model="scope.row.checked"
                  :disabled="scope.row.status === 'error'"
                ></el-checkbox>
              </template>
            </el-table-column>

            <!-- 序號 -->
            <el-table-column type="index" label="#" width="50" align="center" :index="tableRowIndex"></el-table-column>

            <!-- 檢驗狀態標籤 -->
            <el-table-column label="狀態" width="145" align="center">
              <template #default="scope">
                <span v-if="scope.row.status === 'ready' && scope.row.remark" class="badge-pill pill-remark">
                  <i class="el-icon-warning-outline"></i> 可匯入 (含備註)
                </span>
                <span v-else-if="scope.row.status === 'ready'" class="badge-pill pill-ready">
                  <i class="el-icon-check"></i> 可匯入
                </span>
                <span v-else-if="scope.row.status === 'duplicate'" class="badge-pill pill-duplicate">
                  <i class="el-icon-time"></i> 已存在 (跳過)
                </span>
                <span v-else class="badge-pill pill-error">
                  <i class="el-icon-warning"></i> {{ scope.row.statusText }}
                </span>
              </template>
            </el-table-column>

            <!-- 邊坡單元 -->
            <el-table-column
              prop="slopeUnit"
              label="邊坡單元"
              min-width="170"
              show-overflow-tooltip
            >
              <template #default="scope">
                <span class="font-medium text-dark">{{ scope.row.slopeUnit || '-' }}</span>
              </template>
            </el-table-column>

            <!-- 對應測站 (匹配結果) -->
            <el-table-column
              prop="stationName"
              label="對應測站 (Station)"
              min-width="200"
              show-overflow-tooltip
            >
              <template #default="scope">
                <div v-if="scope.row.stationName" class="station-cell">
                  <i class="el-icon-location text-primary"></i>
                  <span>{{ scope.row.stationName }}</span>
                </div>
                <div v-else class="text-danger">
                  <i class="el-icon-close"></i> 未匹配
                </div>
              </template>
            </el-table-column>

            <!-- 儀器名稱 -->
            <el-table-column
              prop="instrumentName"
              label="儀器名稱 (Instrument)"
              min-width="190"
              show-overflow-tooltip
            >
              <template #default="scope">
                <span v-if="scope.row.instrumentId" class="instrument-cell-code font-mono">
                  {{ scope.row.instrumentName }}
                </span>
                <span v-else class="text-danger font-mono">
                  {{ scope.row.instrumentName || '-' }}
                </span>
              </template>
            </el-table-column>

            <!-- 量測時間 -->
            <el-table-column
              prop="timestamp"
              label="量測時間"
              width="165"
              align="center"
            >
              <template #default="scope">
                <span class="font-mono">{{ scope.row.timestamp || '-' }}</span>
              </template>
            </el-table-column>

            <!-- 量測讀值 -->
            <el-table-column
              prop="reading"
              label="量測讀值"
              width="110"
              align="right"
            >
              <template #default="scope">
                <span v-if="scope.row.reading !== null" class="reading-num font-mono">
                  {{ formatReading(scope.row.reading) }}
                </span>
                <span v-else-if="scope.row.remark" class="font-mono text-muted">—</span>
                <span v-else class="text-danger font-mono">無讀值</span>
              </template>
            </el-table-column>

            <!-- 狀態備註 (Remark) -->
            <el-table-column
              prop="remark"
              label="狀態備註"
              width="110"
              align="center"
            >
              <template #default="scope">
                <el-tag
                  v-if="scope.row.remark"
                  size="mini"
                  type="warning"
                  effect="light"
                  class="font-medium"
                >
                  {{ scope.row.remark }}
                </el-tag>
                <span v-else class="text-muted">-</span>
              </template>
            </el-table-column>

            <!-- 讀值定義 -->
            <el-table-column
              prop="readingField"
              label="讀值定義"
              width="120"
              align="center"
            >
              <template #default="scope">
                <el-tag size="mini" type="info" effect="plain">{{ scope.row.readingField }}</el-tag>
              </template>
            </el-table-column>

            <!-- 檢驗訊息 / 說明 -->
            <el-table-column
              prop="message"
              label="檢驗訊息"
              min-width="230"
              show-overflow-tooltip
            >
              <template #default="scope">
                <span :class="getMessageClass(scope.row)">
                  {{ scope.row.message || '匹配檢驗通過' }}
                </span>
              </template>
            </el-table-column>
          </el-table>

          <!-- 分頁控制 -->
          <div class="table-pagination-footer">
            <div class="pagination-summary">
              顯示第 {{ (currentPage - 1) * pageSize + 1 }} 至 {{ Math.min(currentPage * pageSize, filteredRecords.length) }} 筆，共 {{ filteredRecords.length }} 筆
            </div>
            <el-pagination
              background
              layout="sizes, prev, pager, next, jumper"
              :current-page.sync="currentPage"
              :page-size.sync="pageSize"
              :page-sizes="[50, 100, 200, 500]"
              :total="filteredRecords.length"
            ></el-pagination>
          </div>
        </el-card>
      </div>
    </transition>

    <!-- 5. 匯入確認對話框 -->
    <el-dialog
      title="確認執行人工數據匯入"
      :visible.sync="showImportDialog"
      width="500px"
      :close-on-click-modal="false"
      class="import-confirm-dialog"
    >
      <div class="confirm-dialog-body">
        <div class="dialog-hero-box">
          <i class="el-icon-upload2 hero-icon"></i>
          <div class="hero-text">
            <h3>準備匯入至高鐵人工監測專案</h3>
            <p>即將將已驗證通過的數據項目批次寫入資料庫。</p>
          </div>
        </div>

        <div class="dialog-stat-summary">
          <div class="summary-row">
            <span class="label">目標專案：</span>
            <span class="value font-medium">{{ targetProjectName }} ({{ THSR_MANUAL_PROJECT_ID }})</span>
          </div>
          <div class="summary-row">
            <span class="label">儀器類型：</span>
            <span class="value">{{ currentTypeObj.label }}</span>
          </div>
          <div class="summary-row">
            <span class="label">來源工作表：</span>
            <span class="value font-mono">{{ selectedSheet }}</span>
          </div>
          <div class="summary-row highlight-row">
            <span class="label">預計匯入筆數：</span>
            <span class="value text-success font-mono font-bold">{{ selectedCount }} 筆</span>
          </div>
          <div v-if="duplicateCount > 0" class="summary-tip-row">
            <i class="el-icon-info"></i> 已自動排除 {{ duplicateCount }} 筆已存在的重複數據。
          </div>
          <div v-if="selectedRemarkCount > 0" class="summary-tip-row" style="color: #b45309; background: #fffbeb; border-color: #fde68a;">
            <i class="el-icon-warning-outline"></i> 包含 {{ selectedRemarkCount }} 筆狀態備註（將以空值寫入並記錄備註至儀器）。
          </div>
        </div>

        <div v-if="importing" class="progress-wrap">
          <el-progress :percentage="importProgress" :status="importProgress === 100 ? 'success' : undefined" stroke-width="8"></el-progress>
          <div class="progress-caption">
            <i class="el-icon-loading"></i> 資料寫入中，請稍候... ({{ importProgress }}%)
          </div>
        </div>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button @click="showImportDialog = false" :disabled="importing" size="medium">取消</el-button>
        <el-button type="success" :loading="importing" size="medium" @click="executeImport">
          確認送出匯入
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import * as XLSX from 'xlsx';
import moment from 'moment-timezone';
import { THSR_MANUAL_PROJECT_ID } from '@/lib/base/project';

export default {
  name: 'ManualImportTHSR',
  data() {
    return {
      THSR_MANUAL_PROJECT_ID,
      loading: false,
      treeLoading: false,
      parsing: false,
      importing: false,
      importProgress: 0,
      showImportDialog: false,

      // 專案階層快取
      projectStations: [],
      stationMap: new Map(),
      instrumentGlobalMap: new Map(),
      isTreeLoaded: false,

      // 檔案與選項
      file: null,
      fileName: '',
      workbook: null,
      sheetNames: [],
      selectedSheet: '',
      selectedType: 'CG',

      // 支援的 7 種儀器類型設定
      instrumentTypes: [
        { value: 'CG', label: '裂縫計 (CG)', readingDesc: '變位量 (mm)' },
        { value: 'OW', label: '水位觀測井 (OW)', readingDesc: '水位高程 GL.- m / m(-GL)' },
        { value: 'TI', label: '地表傾斜計 (TI)', readingDesc: '傾斜角角度(分)' },
        { value: 'ETI', label: '電子傾斜計 (ETI)', readingDesc: 'TI-X(分)、TI-Y(分)' },
        { value: 'EXT', label: '層別沉陷計 (EXT)', readingDesc: '變化量 (mm)' },
        { value: 'SAA', label: '陣列式傾斜儀 (SAA)', readingDesc: 'X-Disp、Y-Disp (mm)' },
        { value: 'SIS', label: '地中傾斜管 (SIS)', readingDesc: 'A-Disp、B-Disp (mm)' },
      ],

      // 解析後的數據清單
      parsedRecords: [],

      // 篩選與分頁
      activeFilter: 'all',
      searchQuery: '',
      currentPage: 1,
      pageSize: 50,
    };
  },
  computed: {
    targetProject() {
      const list = this.$store.getters['project/projectList'] || [];
      return list.find(p => p._id === this.THSR_MANUAL_PROJECT_ID);
    },
    targetProjectName() {
      return this.targetProject?.project_id || this.targetProject?.name || '高鐵人工監測';
    },
    // 依目前進度動態計算 Element UI Steps 之 active 索引 (0~4)
    currentWorkflowStep() {
      if (this.importProgress === 100) return 4;
      if (this.parsedRecords.length > 0) return 3;
      if (this.selectedSheet && this.workbook) return 2;
      if (this.fileName) return 1;
      return 0;
    },
    currentTypeObj() {
      return this.instrumentTypes.find(t => t.value === this.selectedType) || this.instrumentTypes[0];
    },
    canParse() {
      return !!this.workbook && !!this.selectedSheet && !!this.selectedType;
    },
    filteredRecords() {
      let list = this.parsedRecords;
      if (this.activeFilter === 'ready') {
        list = list.filter(r => r.status === 'ready');
      } else if (this.activeFilter === 'duplicate') {
        list = list.filter(r => r.status === 'duplicate');
      } else if (this.activeFilter === 'error') {
        list = list.filter(r => r.status === 'error');
      }

      if (this.searchQuery) {
        const q = this.searchQuery.trim().toLowerCase();
        list = list.filter(r =>
          (r.slopeUnit && r.slopeUnit.toLowerCase().includes(q)) ||
          (r.stationName && r.stationName.toLowerCase().includes(q)) ||
          (r.instrumentName && r.instrumentName.toLowerCase().includes(q)) ||
          (r.remark && r.remark.toLowerCase().includes(q)) ||
          (r.message && r.message.toLowerCase().includes(q)),
        );
      }
      return list;
    },
    paginatedRecords() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredRecords.slice(start, start + this.pageSize);
    },
    totalCount() {
      return this.parsedRecords.length;
    },
    readyCount() {
      return this.parsedRecords.filter(r => r.status === 'ready').length;
    },
    duplicateCount() {
      return this.parsedRecords.filter(r => r.status === 'duplicate').length;
    },
    errorCount() {
      return this.parsedRecords.filter(r => r.status === 'error').length;
    },
    selectedCount() {
      return this.parsedRecords.filter(r => r.checked && r.status !== 'error').length;
    },
    selectedRemarkCount() {
      return this.parsedRecords.filter(r => r.checked && r.remark).length;
    },
  },
  watch: {
    activeFilter() {
      this.currentPage = 1;
    },
    searchQuery() {
      this.currentPage = 1;
    },
  },
  mounted() {
    this.loadProjectTree();
  },
  methods: {
    tableRowIndex(index) {
      return (this.currentPage - 1) * this.pageSize + index + 1;
    },

    // 1. 載入 THSR_MANUAL_PROJECT_ID 專案結構
    async loadProjectTree() {
      this.treeLoading = true;
      try {
        let manualList = [];
        try {
          const summaryRes = await this.$axios({
            category: 'manual',
            method: 'get',
            apiName: `getManualInstrumentSummary/${THSR_MANUAL_PROJECT_ID}`,
          });
          if (Array.isArray(summaryRes.data) && summaryRes.data.length > 0) {
            manualList = summaryRes.data;
          }
        } catch (e) {
          console.warn('getManualInstrumentSummary 失敗，嘗試備援:', e);
        }

        const stations = [];
        const map = new Map();
        const insGlobalMap = new Map();

        if (manualList.length > 0) {
          manualList.forEach(item => {
            const stName = (item.station || '').trim();
            const insName = (item.name || '').trim();
            if (!insName) return;

            const insObj = {
              _id: item._id,
              name: insName,
              stationName: stName,
              areaName: item.area || '',
            };
            insGlobalMap.set(insName, insObj);

            if (stName) {
              if (!map.has(stName)) {
                const stObj = {
                  _id: null,
                  name: stName,
                  areaName: item.area || '',
                  instruments: [],
                };
                map.set(stName, stObj);
                stations.push(stObj);
              }
              map.get(stName).instruments.push(insObj);
            }
          });
        }

        // 備援方案
        if (stations.length === 0) {
          const res = await this.$axios({
            category: 'project',
            method: 'get',
            apiName: `${THSR_MANUAL_PROJECT_ID}/area-station-instrument`,
          });
          const project = res.data;
          (project.area || []).forEach(a => {
            (a.station || []).forEach(s => {
              const stName = (s.name || '').trim();
              const stItem = {
                _id: s._id,
                name: stName,
                areaName: a.name,
                instruments: (s.instrument || []).map(i => {
                  const insName = (
                    (i.latestVersion && i.latestVersion.name) ||
                    (i.data && i.data[0] && i.data[0].name) ||
                    i.name || ''
                  ).trim();
                  const insObj = {
                    _id: i._id,
                    name: insName,
                    stationName: stName,
                    areaName: a.name,
                  };
                  if (insName) insGlobalMap.set(insName, insObj);
                  return insObj;
                }),
              };
              stations.push(stItem);
              if (stName) map.set(stName, stItem);
            });
          });
        }

        this.projectStations = stations;
        this.stationMap = map;
        this.instrumentGlobalMap = insGlobalMap;
        this.isTreeLoaded = true;
        this.$notifySuccess('載入成功', `成功快取 ${insGlobalMap.size} 個人工儀器 (${stations.length} 個測站)`);
      } catch (err) {
        console.error('載入專案結構失敗:', err);
        this.$notifyError('載入高鐵人工專案結構失敗，請檢查權限或網路');
      } finally {
        this.treeLoading = false;
      }
    },

    // 2. 處理檔案上傳
    handleFileChange(file) {
      if (!file || !file.raw) return;
      this.file = file.raw;
      this.fileName = file.name;

      const upperName = file.name.toUpperCase();
      for (const t of this.instrumentTypes) {
        if (upperName.includes(t.value)) {
          this.selectedType = t.value;
          break;
        }
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          this.workbook = workbook;
          this.sheetNames = workbook.SheetNames || [];

          if (this.sheetNames.includes('TEMPLATE_CHECK')) {
            this.selectedSheet = 'TEMPLATE_CHECK';
          } else if (this.sheetNames.length > 0) {
            this.selectedSheet = this.sheetNames[0];
          }

          this.parsedRecords = [];
          this.$notifySuccess('讀取成功', `成功載入 ${file.name}，包含 ${this.sheetNames.length} 個分頁`);
        } catch (err) {
          console.error('解析 Excel 失敗:', err);
          this.$notifyError('解析 Excel 檔案失敗，請確認檔案格式是否正確');
        }
      };
      reader.readAsArrayBuffer(file.raw);
    },

    handleTypeChange() {
      if (this.parsedRecords.length > 0) {
        this.parsedRecords = [];
      }
    },

    resetAll() {
      this.file = null;
      this.fileName = '';
      this.workbook = null;
      this.sheetNames = [];
      this.selectedSheet = '';
      this.parsedRecords = [];
      this.activeFilter = 'all';
      this.searchQuery = '';
      this.currentPage = 1;
    },

    // 3. 測站搜尋邏輯 (Slope Unit -> Station)
    findStation(slopeUnit, instrumentType) {
      if (!slopeUnit) return null;
      const cleanSlope = String(slopeUnit).trim().replace(/[\u2010-\u2015]/g, '-');
      const directKey = `${cleanSlope}-${instrumentType}`;

      if (this.stationMap.has(directKey)) {
        return this.stationMap.get(directKey);
      }
      if (this.stationMap.has(cleanSlope)) {
        return this.stationMap.get(cleanSlope);
      }
      const lowerTarget = directKey.toLowerCase();
      const found = this.projectStations.find(s => s.name.toLowerCase() === lowerTarget);
      if (found) return found;

      return this.projectStations.find(s =>
        s.name.toLowerCase().startsWith(cleanSlope.toLowerCase()) &&
        s.name.toLowerCase().includes(instrumentType.toLowerCase()),
      ) || null;
    },

    cleanStr(val) {
      if (val === null || val === undefined) return '';
      return String(val).trim();
    },

    cleanNum(val) {
      if (val === null || val === undefined || val === '') return null;
      if (typeof val === 'number') return isFinite(val) ? val : null;
      const s = String(val).replace(/,/g, '').trim();
      const n = Number(s);
      return isFinite(n) && s !== '' ? n : null;
    },

    parseExcelDate(serial) {
      if (!serial && serial !== 0) return null;
      if (typeof serial === 'string') {
        const m = moment.tz(serial, 'Asia/Taipei');
        return m.isValid() ? m.format('YYYY-MM-DD HH:mm:ss') : serial.trim();
      }
      if (typeof serial === 'number') {
        const parsed = XLSX.SSF.parse_date_code(serial);
        if (!parsed) return null;
        const y = parsed.y;
        const m = String(parsed.m).padStart(2, '0');
        const d = String(parsed.d).padStart(2, '0');
        const H = String(parsed.H || 0).padStart(2, '0');
        const M = String(parsed.M || 0).padStart(2, '0');
        const S = String(parsed.S || 0).padStart(2, '0');
        return `${y}-${m}-${d} ${H}:${M}:${S}`;
      }
      return null;
    },

    // 4. 核心解析邏輯
    async parseAndValidateSheet() {
      if (!this.canParse) return;
      if (!this.isTreeLoaded) {
        await this.loadProjectTree();
      }

      this.parsing = true;
      try {
        const ws = this.workbook.Sheets[this.selectedSheet];
        if (!ws) {
          this.$notifyError(`找不到工作表: ${this.selectedSheet}`);
          return;
        }

        const rows = XLSX.utils.sheet_to_json(ws, { header: 1 });
        if (!rows || rows.length === 0) {
          this.$notifyError('工作表內無任何資料列');
          return;
        }

        let rawRecords = [];
        const type = this.selectedType;

        switch (type) {
          case 'CG':
            rawRecords = this.parseCG(rows);
            break;
          case 'OW':
            rawRecords = this.parseOW(rows);
            break;
          case 'TI':
            rawRecords = this.parseTI(rows);
            break;
          case 'ETI':
            rawRecords = this.parseETI(rows);
            break;
          case 'EXT':
            rawRecords = this.parseEXT(rows);
            break;
          case 'SAA':
            rawRecords = this.parseSAA(rows);
            break;
          case 'SIS':
            rawRecords = this.parseSIS(rows);
            break;
        }

        if (rawRecords.length === 0) {
          this.$notifyError('未解析出任何有效數據，請確認工作表格式與所選儀器類型是否吻合');
          this.parsedRecords = [];
          return;
        }

        await this.checkDuplicatesAndFinalize(rawRecords);
        this.currentPage = 1;
        this.$notifySuccess('解析完成', `成功解析 ${this.parsedRecords.length} 筆數據 (可匯入: ${this.readyCount}，重複跳過: ${this.duplicateCount})`);
      } catch (err) {
        console.error('解析發生異常:', err);
        this.$notifyError(`解析發生錯誤: ${err.message}`);
      } finally {
        this.parsing = false;
      }
    },

    // --- 各儀器類型專屬解析器 ---
    parseCG(rows) {
      const records = [];
      let headerRowIdx = -1;
      for (let r = 0; r < Math.min(rows.length, 15); r++) {
        const row = rows[r];
        if (row && row.some(c => String(c).includes('邊坡單元')) && row.some(c => String(c).includes('儀器名稱'))) {
          headerRowIdx = r;
          break;
        }
      }
      if (headerRowIdx === -1) headerRowIdx = 9;

      const header = rows[headerRowIdx] || [];
      const colSlope = header.findIndex(c => String(c).includes('邊坡單元'));
      const colIns = header.findIndex(c => String(c).includes('儀器名稱'));
      
      let colDate = -1;
      let colDisp = -1;
      for (let c = header.length - 1; c >= 0; c--) {
        const val = String(header[c]);
        if (colDisp === -1 && (val.includes('變位量') || val.includes('位移量'))) colDisp = c;
        if (colDate === -1 && val.includes('日期')) colDate = c;
      }
      if (colSlope === -1 || colIns === -1) return [];
      if (colDate === -1) colDate = 7;
      if (colDisp === -1) colDisp = 10;

      for (let r = headerRowIdx + 1; r < rows.length; r++) {
        const row = rows[r];
        if (!row || (!row[colSlope] && !row[colIns])) continue;

        const slopeUnit = this.cleanStr(row[colSlope]);
        const insName = this.cleanStr(row[colIns]);
        const dateStr = this.parseExcelDate(row[colDate]);
        const rawReading = row[colDisp];
        const numReading = this.cleanNum(rawReading);

        records.push(this.createRecordItem({
          slopeUnit,
          targetStationName: `${slopeUnit}-CG`,
          targetInsName: insName,
          timestamp: dateStr,
          rawReading,
          numReading,
          readingField: '變位量',
        }));
      }
      return records;
    },

    parseOW(rows) {
      let summaryHeaderIdx = -1;
      for (let r = 0; r < Math.min(rows.length, 10); r++) {
        const row = rows[r];
        if (row && (row.some(c => String(c).includes('量測水位')) || row.some(c => String(c).includes('水位高程')))) {
          summaryHeaderIdx = r;
          break;
        }
      }

      if (summaryHeaderIdx !== -1) {
        const header = rows[summaryHeaderIdx];
        const colDate = header.findIndex(c => String(c).includes('日期'));
        const colIns = header.findIndex(c => String(c).includes('儀器名稱'));
        const colSlope = header.findIndex(c => String(c).includes('邊坡單元'));
        const colElev = header.findIndex(c => String(c).includes('水位高程'));

        const records = [];
        for (let r = summaryHeaderIdx + 1; r < rows.length; r++) {
          const row = rows[r];
          if (!row || (!row[colIns] && !row[colSlope])) continue;

          const slopeUnit = this.cleanStr(row[colSlope]);
          const insName = this.cleanStr(row[colIns]);
          const dateStr = this.parseExcelDate(row[colDate !== -1 ? colDate : 0]);
          const rawReading = row[colElev !== -1 ? colElev : 5];
          const numReading = this.cleanNum(rawReading);

          records.push(this.createRecordItem({
            slopeUnit,
            targetStationName: `${slopeUnit}-OW`,
            targetInsName: insName,
            timestamp: dateStr,
            rawReading,
            numReading,
            readingField: '水位高程',
          }));
        }
        return records;
      } else {
        let insName = '';
        let slopeUnit = '';
        for (let r = 0; r < Math.min(rows.length, 6); r++) {
          const row = rows[r];
          if (!row) continue;
          for (let c = 0; c < row.length; c++) {
            if (String(row[c]).includes('儀器名稱') && row[c + 1]) insName = this.cleanStr(row[c + 1]);
            if (String(row[c]).includes('邊坡單元') && row[c + 1]) slopeUnit = this.cleanStr(row[c + 1]);
          }
        }

        let dataStartIdx = 3;
        let colDate = 1;
        let colReading = 3;
        for (let r = 0; r < Math.min(rows.length, 6); r++) {
          const row = rows[r];
          if (row && row.some(c => String(c).toLowerCase().includes('date') || String(c).includes('m(-gl)'))) {
            colDate = row.findIndex(c => String(c).toLowerCase().includes('date'));
            colReading = row.findIndex(c => String(c).toLowerCase().includes('m(-gl)'));
            dataStartIdx = r + 1;
            break;
          }
        }
        if (colDate === -1) colDate = 1;
        if (colReading === -1) colReading = 3;

        const records = [];
        for (let r = dataStartIdx; r < rows.length; r++) {
          const row = rows[r];
          if (!row || row[colDate] === undefined || row[colDate] === null) continue;

          const dateStr = this.parseExcelDate(row[colDate]);
          const rawReading = row[colReading];
          const numReading = this.cleanNum(rawReading);

          records.push(this.createRecordItem({
            slopeUnit,
            targetStationName: `${slopeUnit}-OW`,
            targetInsName: insName,
            timestamp: dateStr,
            rawReading,
            numReading,
            readingField: 'm(-GL)',
          }));
        }
        return records;
      }
    },

    parseTI(rows) {
      const records = [];
      let headerRowIdx = -1;
      for (let r = 0; r < Math.min(rows.length, 12); r++) {
        const row = rows[r];
        if (row && row.some(c => String(c).includes('儀器名稱')) && row.some(c => String(c).includes('邊坡單元'))) {
          headerRowIdx = r;
          break;
        }
      }
      if (headerRowIdx === -1) headerRowIdx = 7;

      const header = rows[headerRowIdx] || [];
      const colDate = header.findIndex(c => String(c).includes('日期'));
      const colIns = header.findIndex(c => String(c).includes('儀器名稱'));
      const colSlope = header.findIndex(c => String(c).includes('邊坡單元'));
      const colTilt = header.findIndex(c => String(c).includes('傾斜角') || String(c).includes('角度(分)'));

      for (let r = headerRowIdx + 1; r < rows.length; r++) {
        const row = rows[r];
        if (!row || (!row[colIns] && !row[colSlope])) continue;

        const dateStr = this.parseExcelDate(row[colDate !== -1 ? colDate : 0]);
        const insName = this.cleanStr(row[colIns !== -1 ? colIns : 1]);
        const slopeUnit = this.cleanStr(row[colSlope !== -1 ? colSlope : 2]);
        const rawReading = row[colTilt !== -1 ? colTilt : 10];
        const numReading = this.cleanNum(rawReading);

        records.push(this.createRecordItem({
          slopeUnit,
          targetStationName: `${slopeUnit}-TI`,
          targetInsName: insName,
          timestamp: dateStr,
          rawReading,
          numReading,
          readingField: '傾斜角角度(分)',
        }));
      }
      return records;
    },

    parseETI(rows) {
      let insName = '';
      let slopeUnit = '';
      for (let r = 0; r < Math.min(rows.length, 10); r++) {
        const row = rows[r];
        if (!row) continue;
        for (let c = 0; c < row.length; c++) {
          if (String(row[c]).includes('儀器名稱') && row[c + 1]) insName = this.cleanStr(row[c + 1]);
          if (String(row[c]).includes('邊坡單元') && row[c + 1]) slopeUnit = this.cleanStr(row[c + 1]);
        }
      }

      let headerRowIdx = 8;
      let colX = 1;
      let colY = 2;
      for (let r = 0; r < Math.min(rows.length, 12); r++) {
        const row = rows[r];
        if (row && row.some(c => String(c).includes('TI-X') || String(c).includes('TI-Y'))) {
          colX = row.findIndex(c => String(c).includes('TI-X'));
          colY = row.findIndex(c => String(c).includes('TI-Y'));
          headerRowIdx = r;
          break;
        }
      }

      const records = [];
      for (let r = headerRowIdx + 1; r < rows.length; r++) {
        const row = rows[r];
        if (!row || row[0] === undefined || row[0] === null) continue;

        const dateStr = this.parseExcelDate(row[0]);
        if (!dateStr) continue;

        const rawX = row[colX];
        records.push(this.createRecordItem({
          slopeUnit,
          targetStationName: `${slopeUnit}-ETI`,
          targetInsName: `${insName}-X`,
          timestamp: dateStr,
          rawReading: rawX,
          numReading: this.cleanNum(rawX),
          readingField: 'TI-X(分)',
        }));

        const rawY = row[colY];
        records.push(this.createRecordItem({
          slopeUnit,
          targetStationName: `${slopeUnit}-ETI`,
          targetInsName: `${insName}-Y`,
          timestamp: dateStr,
          rawReading: rawY,
          numReading: this.cleanNum(rawY),
          readingField: 'TI-Y(分)',
        }));
      }
      return records;
    },

    parseEXT(rows) {
      let insName = '';
      let slopeUnit = '';
      for (let r = 0; r < Math.min(rows.length, 8); r++) {
        const row = rows[r];
        if (!row) continue;
        for (let c = 0; c < row.length; c++) {
          if (String(row[c]).includes('儀器名稱') && row[c + 1]) insName = this.cleanStr(row[c + 1]);
          if (String(row[c]).includes('邊坡單元') && row[c + 1]) slopeUnit = this.cleanStr(row[c + 1]);
        }
      }

      const layers = [];
      let dataStartIdx = 11;
      for (let r = 0; r < Math.min(rows.length, 12); r++) {
        const row = rows[r];
        if (!row) continue;
        for (let c = 0; c < row.length; c++) {
          const str = String(row[c]);
          if (str.includes('安裝深度') || str.includes('EXT1-') || str.includes('EXT2-')) {
            const m = str.match(/(\d+(\.\d+)?)m/);
            if (m) {
              const depth = parseFloat(m[1]);
              let dispCol = c + 1;
              for (let lookR = r; lookR <= r + 2 && lookR < rows.length; lookR++) {
                if (rows[lookR] && rows[lookR][c + 1] && String(rows[lookR][c + 1]).includes('變化量')) {
                  dispCol = c + 1;
                  break;
                }
              }
              layers.push({ depth, depthStr: `${depth}m`, col: dispCol });
            }
          }
        }
        if (row.some(c => String(c).includes('量測值') || String(c).includes('變化量'))) {
          dataStartIdx = r + 1;
        }
      }

      if (layers.length === 0) {
        layers.push({ depth: 7, depthStr: '7m', col: 2 });
        layers.push({ depth: 20, depthStr: '20m', col: 4 });
      }

      const records = [];
      for (let r = dataStartIdx; r < rows.length; r++) {
        const row = rows[r];
        if (!row || row[0] === undefined || row[0] === null) continue;

        const dateStr = this.parseExcelDate(row[0]);
        if (!dateStr) continue;

        for (const layer of layers) {
          const rawDisp = row[layer.col];
          records.push(this.createRecordItem({
            slopeUnit,
            targetStationName: `${slopeUnit}-EXT`,
            targetInsName: `${insName}`,
            extraDepthMatch: layer.depthStr,
            timestamp: dateStr,
            rawReading: rawDisp,
            numReading: this.cleanNum(rawDisp),
            readingField: `變化量 (${layer.depthStr})`,
          }));
        }
      }
      return records;
    },

    parseSAA(rows) {
      let insName = '';
      let slopeUnit = '';
      let dateStr = null;

      for (let r = 0; r < Math.min(rows.length, 12); r++) {
        const row = rows[r];
        if (!row) continue;
        for (let c = 0; c < row.length; c++) {
          if (String(row[c]).includes('儀器名稱') && row[c + 1]) insName = this.cleanStr(row[c + 1]);
          if (String(row[c]).includes('邊坡單元') && row[c + 1]) slopeUnit = this.cleanStr(row[c + 1]);
          if (String(row[c]).includes('觀測日期') && row[c + 1]) dateStr = this.parseExcelDate(row[c + 1]);
        }
      }

      let dataStartIdx = 13;
      let colDepth = 0, colXDisp = 5, colYDisp = 6;
      for (let r = 0; r < Math.min(rows.length, 14); r++) {
        const row = rows[r];
        if (row && row.some(c => String(c).includes('DEPTH') || String(c).includes('X-Disp'))) {
          colDepth = row.findIndex(c => String(c).includes('DEPTH'));
          colXDisp = row.findIndex(c => String(c).includes('X-Disp'));
          colYDisp = row.findIndex(c => String(c).includes('Y-Disp'));
          dataStartIdx = r + 1;
          break;
        }
      }
      if (colDepth === -1) colDepth = 0;
      if (colXDisp === -1) colXDisp = 5;
      if (colYDisp === -1) colYDisp = 6;

      const records = [];
      for (let r = dataStartIdx; r < rows.length; r++) {
        const row = rows[r];
        if (!row || row[colDepth] === undefined || row[colDepth] === null || row[colDepth] === '') continue;

        const rawDepth = row[colDepth];
        const depthNum = Math.abs(Number(rawDepth));
        if (isNaN(depthNum)) continue;
        const depthStr = `${depthNum}m`;

        const rawX = row[colXDisp];
        records.push(this.createRecordItem({
          slopeUnit,
          targetStationName: `${slopeUnit}-SAA`,
          targetInsName: `${insName}-X-${depthStr}`,
          timestamp: dateStr,
          rawReading: rawX,
          numReading: this.cleanNum(rawX),
          readingField: `X-Disp (${depthStr})`,
        }));

        const rawY = row[colYDisp];
        records.push(this.createRecordItem({
          slopeUnit,
          targetStationName: `${slopeUnit}-SAA`,
          targetInsName: `${insName}-Y-${depthStr}`,
          timestamp: dateStr,
          rawReading: rawY,
          numReading: this.cleanNum(rawY),
          readingField: `Y-Disp (${depthStr})`,
        }));
      }
      return records;
    },

    parseSIS(rows) {
      let insName = '';
      let slopeUnit = '';
      let dateStr = null;

      for (let r = 0; r < Math.min(rows.length, 12); r++) {
        const row = rows[r];
        if (!row) continue;
        for (let c = 0; c < row.length; c++) {
          if (String(row[c]).includes('儀器名稱') && row[c + 1]) insName = this.cleanStr(row[c + 1]);
          if (String(row[c]).includes('邊坡單元') && row[c + 1]) slopeUnit = this.cleanStr(row[c + 1]);
          if (String(row[c]).includes('觀測日期') && row[c + 1]) dateStr = this.parseExcelDate(row[c + 1]);
        }
      }

      let dataStartIdx = 13;
      let colDepth = 0, colADisp = 9, colBDisp = 10;
      for (let r = 0; r < Math.min(rows.length, 14); r++) {
        const row = rows[r];
        if (row && row.some(c => String(c).includes('DEPTH') || String(c).includes('A-Disp'))) {
          colDepth = row.findIndex(c => String(c).includes('DEPTH'));
          colADisp = row.findIndex(c => String(c).includes('A-Disp'));
          colBDisp = row.findIndex(c => String(c).includes('B-Disp'));
          dataStartIdx = r + 1;
          break;
        }
      }
      if (colDepth === -1) colDepth = 0;
      if (colADisp === -1) colADisp = 9;
      if (colBDisp === -1) colBDisp = 10;

      const records = [];
      for (let r = dataStartIdx; r < rows.length; r++) {
        const row = rows[r];
        if (!row || row[colDepth] === undefined || row[colDepth] === null || row[colDepth] === '') continue;

        const rawDepth = row[colDepth];
        const depthNum = Math.abs(Number(rawDepth));
        if (isNaN(depthNum)) continue;
        const depthStr = `${depthNum}m`;

        const rawA = row[colADisp];
        records.push(this.createRecordItem({
          slopeUnit,
          targetStationName: `${slopeUnit}-SIS`,
          targetInsName: `${insName}-X-${depthStr}`,
          timestamp: dateStr,
          rawReading: rawA,
          numReading: this.cleanNum(rawA),
          readingField: `A-Disp (${depthStr})`,
        }));

        const rawB = row[colBDisp];
        records.push(this.createRecordItem({
          slopeUnit,
          targetStationName: `${slopeUnit}-SIS`,
          targetInsName: `${insName}-Y-${depthStr}`,
          timestamp: dateStr,
          rawReading: rawB,
          numReading: this.cleanNum(rawB),
          readingField: `B-Disp (${depthStr})`,
        }));
      }
      return records;
    },

    // 建立記錄項並執行測站/儀器匹配
    createRecordItem({ slopeUnit, targetStationName, targetInsName, extraDepthMatch, timestamp, rawReading, numReading, readingField }) {
      let station = this.findStation(slopeUnit, this.selectedType);
      let instrument = null;
      let status = 'ready';
      let statusText = '可匯入';
      let message = '';

      if (!station) {
        // 全域字典反查
        const globalMatch = this.instrumentGlobalMap.get(targetInsName);
        if (globalMatch) {
          station = this.stationMap.get(globalMatch.stationName) || { name: globalMatch.stationName, _id: null, instruments: [globalMatch] };
          instrument = globalMatch;
          message = `已依儀器名稱自動對應至測站 [${globalMatch.stationName}]`;
        } else {
          status = 'error';
          statusText = '找不到測站';
          message = `在專案中找不到邊坡對應測站 [${targetStationName}]`;
        }
      } else {
        if (extraDepthMatch) {
          instrument = station.instruments.find(i =>
            i.name.includes(targetInsName) && i.name.includes(extraDepthMatch),
          ) || station.instruments.find(i => i.name.includes(extraDepthMatch));
        } else {
          instrument = station.instruments.find(i => i.name === targetInsName);
          if (!instrument) {
            instrument = station.instruments.find(i => i.name.toLowerCase() === targetInsName.toLowerCase());
          }
          if (!instrument) {
            const normTarget = targetInsName.replace(/[\s_-]/g, '').toLowerCase();
            instrument = station.instruments.find(i => i.name.replace(/[\s_-]/g, '').toLowerCase() === normTarget);
          }
        }

        // 全域補救
        if (!instrument && this.instrumentGlobalMap.has(targetInsName)) {
          const globalMatch = this.instrumentGlobalMap.get(targetInsName);
          instrument = globalMatch;
          message = `已依儀器名稱自動匹配 (登記於測站 [${globalMatch.stationName}])`;
        }

        if (!instrument) {
          status = 'error';
          statusText = '找不到儀器';
          message = `測站 [${station.name}] 下找不到儀器 [${targetInsName}]`;
        }
      }

      // 檢查讀值與時間
      let remark = '';
      if (status !== 'error') {
        if (!timestamp) {
          status = 'error';
          statusText = '無時間';
          message = '未提供有效量測時間';
        } else if (numReading === null) {
          // 選項 A：若儲存格具有非數值文字（例如「破壞」、「損壞」），轉為備註並以空值寫入
          const textRemark = (rawReading !== null && rawReading !== undefined) ? String(rawReading).trim() : '';
          if (textRemark !== '') {
            remark = textRemark;
            status = 'ready';
            statusText = '可匯入 (含備註)';
            message = message
              ? `${message}；讀值非有效數值 [${textRemark}]，轉為備註並以空值匯入`
              : `讀值非有效數值 [${textRemark}]，轉為備註並以空值匯入`;
          } else {
            status = 'error';
            statusText = '讀值空白';
            message = '未填寫量測讀值 (空白)';
          }
        }
      }

      return {
        id: Math.random().toString(36).substr(2, 9),
        status,
        statusText,
        slopeUnit,
        stationName: station ? station.name : '',
        stationId: station ? station._id : null,
        instrumentName: instrument ? instrument.name : targetInsName,
        instrumentId: instrument ? instrument._id : null,
        timestamp,
        reading: numReading,
        rawReading,
        remark,
        readingField,
        message,
        checked: status === 'ready',
      };
    },

    // 5. 批次呼叫重複檢查 API (Pre-check duplicates)
    async checkDuplicatesAndFinalize(records) {
      const readyItems = records.filter(r => r.status === 'ready' && r.instrumentId && r.timestamp);
      if (readyItems.length === 0) {
        this.parsedRecords = records;
        return;
      }

      try {
        const checkPayload = readyItems.map(r => ({
          instrument_id: r.instrumentId,
          timestamp: r.timestamp,
        }));

        const res = await this.$axios({
          category: 'manual',
          method: 'post',
          apiName: 'checkDuplicates',
          data: { items: checkPayload },
        });

        const existingList = (res.data && res.data.existing) || [];
        const existingSet = new Set(
          existingList.map(it => `${it.instrument_id}_${it.timestamp}`),
        );

        records.forEach(r => {
          if (r.status === 'ready') {
            const key = `${r.instrumentId}_${r.timestamp}`;
            if (existingSet.has(key)) {
              r.status = 'duplicate';
              r.statusText = '已存在 (跳過)';
              r.message = '資料庫中已存在相同時間數據 (預設跳過)';
              r.checked = false;
            }
          }
        });
      } catch (err) {
        console.warn('重複檢查失敗，略過檢查:', err);
      }

      this.parsedRecords = records;
    },

    toggleSelectAll(checked) {
      this.filteredRecords.forEach(r => {
        if (r.status !== 'error') {
          r.checked = checked;
        }
      });
    },

    formatReading(val) {
      if (val === null || val === undefined) return '-';
      const num = Number(val);
      if (isNaN(num)) return String(val);
      return Number.isInteger(num) ? num : num.toFixed(3).replace(/\.?0+$/, '');
    },

    getMessageClass(row) {
      if (row.status === 'error') return 'text-danger font-medium';
      if (row.status === 'duplicate') return 'text-warning font-medium';
      if (row.remark) return 'text-warning font-medium';
      if (row.message && row.message.includes('自動對應')) return 'text-primary';
      return 'text-muted';
    },

    tableRowClassName({ row }) {
      if (row.status === 'error') return 'row-error';
      if (row.status === 'duplicate') return 'row-duplicate';
      return '';
    },

    // 6. 送出匯入
    openImportConfirmDialog() {
      if (this.selectedCount === 0) {
        this.$notifyError('請至少選取一筆要匯入的資料');
        return;
      }
      this.importProgress = 0;
      this.showImportDialog = true;
    },

    async executeImport() {
      const itemsToImport = this.parsedRecords.filter(r => r.checked && r.status !== 'error' && r.instrumentId);
      if (itemsToImport.length === 0) return;

      this.importing = true;
      this.importProgress = 10;

      try {
        const timeMap = new Map();
        itemsToImport.forEach(r => {
          const t = r.timestamp;
          if (!timeMap.has(t)) {
            timeMap.set(t, []);
          }
          timeMap.get(t).push({
            instrument: { _id: r.instrumentId },
            reading1: r.reading,
            reading2: r.reading,
            reading3: r.reading,
            remark: r.remark || '',
          });
        });

        const fullPayload = [];
        timeMap.forEach((instrument_data, timestamp) => {
          fullPayload.push({
            timestamp,
            instrument_data,
          });
        });

        const batchSize = 300;
        const totalBatches = Math.ceil(fullPayload.length / batchSize);

        for (let i = 0; i < totalBatches; i++) {
          const batch = fullPayload.slice(i * batchSize, (i + 1) * batchSize);
          await this.$axios({
            category: 'manual',
            method: 'put',
            apiName: 'importManualOutput',
            data: batch,
          });
          this.importProgress = Math.min(95, Math.round(((i + 1) / totalBatches) * 100));
        }

        this.importProgress = 100;
        this.$notifySuccess('匯入成功', `成功匯入 ${itemsToImport.length} 筆數據至高鐵人工專案！`);

        itemsToImport.forEach(r => {
          r.status = 'duplicate';
          r.statusText = '已匯入';
          r.message = '本次已成功匯入至資料庫';
          r.checked = false;
        });

        setTimeout(() => {
          this.showImportDialog = false;
        }, 800);
      } catch (err) {
        console.error('匯入數據失敗:', err);
        this.$notifyError('匯入失敗，請確認後端狀態或稍後再試');
      } finally {
        this.importing = false;
      }
    },
  },
};
</script>

<style scoped>
.thsr-import-page {
  padding: 20px;
  background-color: #f0f2f5;
  min-height: calc(100vh - 84px);
}

/* 1. 系統頂部標頭 (Element UI 經典卡片) */
.header-card {
  margin-bottom: 20px;
  border-radius: 4px;
  border: 1px solid #EBEEF5;
  background: #ffffff;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.header-left {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.header-title-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.header-title-icon {
  font-size: 24px;
  color: #409EFF;
}

.main-title {
  font-size: 20px;
  font-weight: 600;
  color: #303133;
  margin: 0;
}

.meta-info-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.meta-tag {
  font-size: 12px;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

/* 2. Steps 流程卡片 */
.steps-card {
  margin-bottom: 20px;
  border-radius: 4px;
  border: 1px solid #EBEEF5;
  background: #ffffff;
  padding: 10px 16px;
}

.custom-steps ::v-deep .el-step__title {
  font-size: 14px;
}

.custom-steps ::v-deep .el-step__description {
  font-size: 12px;
  color: #909399;
}

.custom-steps ::v-deep .is-process .el-step__title {
  color: #409EFF;
  font-weight: 600;
}

.custom-steps ::v-deep .is-process .el-step__head {
  color: #409EFF;
  border-color: #409EFF;
}

.custom-steps ::v-deep .is-success .el-step__title {
  color: #67C23A;
}

.custom-steps ::v-deep .is-success .el-step__head {
  color: #67C23A;
  border-color: #67C23A;
}

/* 3. 配置面板卡片 */
.config-section {
  margin-bottom: 20px;
}

.panel-card {
  border-radius: 4px;
  border: 1px solid #EBEEF5;
  background: #ffffff;
  height: 100%;
}

.panel-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.panel-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  display: flex;
  align-items: center;
  gap: 8px;
}

.panel-title i {
  color: #409EFF;
}

.panel-header-tip {
  font-size: 12px;
  color: #606266;
}

.panel-body-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* 步驟 1 上傳區塊 - 確保無溢出並具有充裕邊距 */
.upload-drop-zone {
  width: 100%;
}

.upload-drop-zone ::v-deep .el-upload {
  width: 100%;
  display: block;
}

.upload-drop-zone ::v-deep .el-upload-dragger {
  width: 100% !important;
  height: auto !important;
  min-height: 130px !important;
  padding: 24px 16px !important;
  display: flex !important;
  flex-direction: column !important;
  justify-content: center !important;
  align-items: center !important;
  box-sizing: border-box !important;
  border: 1px dashed #d9d9d9 !important;
  border-radius: 4px !important;
  background-color: #fafafa !important;
  transition: border-color 0.2s ease, background-color 0.2s ease;
  overflow: hidden !important;
}

.upload-drop-zone ::v-deep .el-upload-dragger:hover {
  border-color: #409EFF !important;
  background-color: #ecf5ff !important;
}

.upload-drop-zone ::v-deep .el-upload-dragger .el-icon-upload {
  font-size: 40px !important;
  color: #409EFF !important;
  margin: 0 0 10px 0 !important;
  line-height: 1 !important;
}

.upload-drop-zone ::v-deep .el-upload-dragger .el-upload__text {
  font-size: 13px !important;
  color: #606266 !important;
  line-height: 1.5 !important;
  margin: 0 !important;
}

.upload-drop-zone ::v-deep .el-upload-dragger .el-upload__text em {
  color: #409EFF !important;
  font-style: normal !important;
  font-weight: 600 !important;
}

.compact-drag {
  width: 100%;
}

.selected-file-badge {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #ecf5ff;
  border: 1px solid #d9ecff;
  border-radius: 4px;
  padding: 10px 14px;
}

.file-info-col {
  display: flex;
  align-items: center;
  gap: 10px;
  overflow: hidden;
}

.file-icon {
  font-size: 22px;
  color: #409EFF;
}

.file-name {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-sub {
  font-size: 12px;
  color: #909399;
}

.form-item-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-label {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-size: 13px;
  font-weight: 600;
  color: #303133;
}

.label-sub {
  font-size: 12px;
  color: #909399;
  font-weight: normal;
}

.type-option-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.type-name {
  font-weight: 500;
}

.type-tag {
  font-size: 11px;
}

/* 步驟 2 讀值對照提示卡片 */
.rules-card-box {
  background: #fafafa;
  border: 1px solid #EBEEF5;
  border-left: 3px solid #409EFF;
  border-radius: 4px;
  padding: 12px 14px;
}

.rules-title {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.rules-title i {
  color: #409EFF;
}

.rules-content {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
}

.rule-line {
  display: flex;
  gap: 8px;
}

.rule-k {
  color: #909399;
  min-width: 100px;
}

.rule-v {
  color: #606266;
}

.parse-action-row {
  margin-top: 4px;
}

.action-submit-btn {
  width: 100%;
  height: 40px;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.5px;
}

/* 4. 結果指標儀表板 */
.results-dashboard-section {
  margin-top: 10px;
}

.metrics-row {
  margin-bottom: 18px;
}

.metric-box {
  background: #ffffff;
  border-radius: 4px;
  padding: 16px 18px;
  border: 1px solid #EBEEF5;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.metric-box:hover {
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.metric-box.active-filter {
  border-width: 2px;
}

.box-total.active-filter { border-color: #409EFF; background: #ecf5ff; }
.box-ready.active-filter { border-color: #67C23A; background: #f0f9eb; }
.box-duplicate.active-filter { border-color: #E6A23C; background: #fdf6ec; }
.box-error.active-filter { border-color: #F56C6C; background: #fef0f0; }

.metric-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.metric-label {
  font-size: 13px;
  font-weight: 500;
  color: #606266;
}

.metric-icon {
  font-size: 20px;
  color: #909399;
}

.box-total .metric-icon { color: #409EFF; }
.box-ready .metric-icon { color: #67C23A; }
.box-duplicate .metric-icon { color: #E6A23C; }
.box-error .metric-icon { color: #F56C6C; }

.metric-value {
  font-size: 26px;
  font-weight: 700;
  color: #303133;
  margin: 2px 0;
}

.metric-bottom {
  font-size: 12px;
  color: #909399;
}

/* 5. 數據表格與工具列 */
.table-card {
  border-radius: 4px;
  border: 1px solid #EBEEF5;
  background: #ffffff;
}

.table-toolbar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-title i {
  color: #409EFF;
}

.toolbar-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.main-import-btn {
  font-weight: 500;
}

.refined-data-table {
  border-radius: 4px;
  overflow: hidden;
}

.refined-data-table ::v-deep th {
  background-color: #fafafa !important;
  color: #606266 !important;
  font-weight: 600 !important;
  font-size: 13px !important;
}

.badge-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 4px;
  line-height: 20px;
}

.pill-ready {
  background: #f0f9eb;
  border: 1px solid #e1f3d8;
  color: #67C23A;
}

.pill-remark {
  background: #fdf6ec;
  border: 1px solid #faecd8;
  color: #E6A23C;
}

.pill-duplicate {
  background: #fdf6ec;
  border: 1px solid #faecd8;
  color: #E6A23C;
}

.pill-error {
  background: #fef0f0;
  border: 1px solid #fde2e2;
  color: #F56C6C;
}

.station-cell {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
  color: #409EFF;
}

.instrument-cell-code {
  font-weight: 500;
  color: #303133;
}

.reading-num {
  font-weight: 600;
  color: #303133;
  font-size: 13px;
}

.table-pagination-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 16px;
}

.pagination-summary {
  font-size: 12px;
  color: #909399;
}

/* 6. 確認對話框 */
.dialog-hero-box {
  display: flex;
  align-items: center;
  gap: 16px;
  background: #ecf5ff;
  border: 1px solid #d9ecff;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 16px;
}

.hero-icon {
  font-size: 36px;
  color: #409EFF;
}

.hero-text h3 {
  margin: 0 0 4px 0;
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.hero-text p {
  margin: 0;
  font-size: 12px;
  color: #606266;
}

.dialog-stat-summary {
  background: #fafafa;
  border: 1px solid #EBEEF5;
  border-radius: 4px;
  padding: 14px 18px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 13px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
}

.summary-row .label {
  color: #909399;
}

.summary-row .value {
  color: #303133;
}

.highlight-row {
  border-top: 1px dashed #dcdfe6;
  padding-top: 8px;
  margin-top: 2px;
}

.summary-tip-row {
  font-size: 12px;
  color: #E6A23C;
  background: #fdf6ec;
  border: 1px solid #faecd8;
  padding: 6px 10px;
  border-radius: 4px;
  margin-top: 4px;
}

.progress-wrap {
  margin-top: 18px;
}

.progress-caption {
  text-align: center;
  font-size: 12px;
  color: #909399;
  margin-top: 8px;
}

/* 輔助類 */
.font-mono { font-family: Consolas, Monaco, monospace; }
.font-bold { font-weight: 700; }
.font-medium { font-weight: 500; }
.text-dark { color: #303133; }
.text-primary { color: #409EFF; }
.text-success { color: #67C23A; }
.text-warning { color: #E6A23C; }
.text-danger { color: #F56C6C; }
.text-muted { color: #909399; }

::v-deep .row-error {
  background-color: #fef0f0 !important;
}

::v-deep .row-duplicate {
  background-color: #fdf6ec !important;
}
</style>
